import { useEffect, useState } from 'react';
import {
	Typography,
	Divider,
	Grid,
	CircularProgress,
	CardContent,
	Stack,
	FormHelperText,
	Link,
} from '@mui/material';
import editIcon from '../../assets/images/icons/editIcon.svg';
import { updateQuote } from '../../helper/formatQuoteForUpdate';
import { Box } from '@mui/system';
import { MixpanelWrapper } from '../MixPanelWrapper';
import { ProductCard } from '../ProductCard';
import EditIcon from '../icons/EditIcon';
import { colours } from '../../shared/colors';
import CreateQuoteCardHeader from '../CreateQuoteCardHeader';
import { useQuoteContext } from '../../shared/contexts/QuoteContext';
import { useGetProductsByEntityCodeQuery } from '../../services/features/productApi/productApi';
import { Product, Package } from '../../shared/types/quotes';
import { FormikConfig, FormikValues } from 'formik';
import { useDispatch } from 'react-redux';
import { openDialog } from '../../services/features/dialogSlice/dialogSlice';
import { useUpdateQuoteGroupMutation } from '../../services/features/quoteApi/quoteApi';

/**
 * ProductSelection component for selecting products and packages.
 *
 * @param {Object} props - The properties passed down to the component.
 * @param {boolean} props.productValidationPass - If the product selection validation has passed.
 * @param {Object} props.options - Additional options for the component.
 * @param {Object} props.options.values - Values for fetching the products based on criteria.
 * @param {Function} props.options.handleChange - Handler to manage changes in the product selection.
 * @returns {JSX.Element} - React JSX element representing the product selection component.
 */
const ProductSelection = (props: any) => {
	// const [loading, setLoading] = useState(false);
	const dispatch = useDispatch();

	const {
		quoteGroup,
		loadingProducts,
		isQuoteInProgress,
		isReferralApprovedModifiable,
		isReferred,
		isDeclined,
	} = useQuoteContext();

	const [updateQuoteGroup, { isLoading: isLoadingUpdate }] = useUpdateQuoteGroupMutation();

	const readonly = !isQuoteInProgress && !isReferralApprovedModifiable;
	const formik = props.options;

	const suboccupationId = parseInt(formik.values.suboccupationId);
	const productQuery = useGetProductsByEntityCodeQuery(
		{
			suboccupationId,
			entityCode: formik.values.entityCode,
		},
		{
			skip:
				!formik.values.suboccupationId ||
				isNaN(suboccupationId) ||
				(formik.values.entityCode ?? '').length === 0,
		}
	);

	useEffect(
		() => console.log({ suboccupationId, code: formik.values.entityCode }),
		[formik.values.entityCode, suboccupationId]
	);
	const products: Product[] = productQuery.data?.products ?? [];
	const packages: Package[] = productQuery.data?.packages ?? [];

	useEffect(() => {
		MixpanelWrapper.track('Create Quote - Selected Products');
	}, []);

	const selectItem = async (item: any, type: 'packages' | 'products') => {
		const selectedItems = formik.values[type];

		let isSelected = selectedItems.map((i: { id: any }) => i.id).includes(item.id);
		let selectedArray: Product[] | Package[] = [];

		if (isSelected) {
			selectedArray = [...selectedItems].filter((i) => i.id !== item.id);
		} else {
			selectedArray = [...selectedItems, item];
		}

		const handleUpdate = async () => {
			if (quoteGroup) {
				const update = await updateQuote(
					quoteGroup,
					type === 'products' ? selectedArray : formik.values.products,
					type === 'packages' ? selectedArray : formik.values.packages
				);
				await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
					.unwrap()
					.catch((error) => console.log('Error in removing product', error));
			}
		};
		const newFunction = async () => {
			formik.setFieldValue(type, selectedArray);
			await handleUpdate();
			props.setRemoveProductClose(() => Promise.resolve());
			props.setNeedsProductRefresh(true);
		};

		if (quoteGroup && formik.values[type].length > selectedArray.length) {
			dispatch(openDialog({ key: 'removeProduct', params: { productName: item?.name } }));
			props.setRemoveProductClose(() => newFunction);
		} else {
			formik.setFieldValue(type, selectedArray);
			await handleUpdate();
			props.setNeedsProductRefresh(true);
		}
		// if (
		// 	quoteGroup &&
		// 	selectedArray.length === 0 &&
		// 	formik.values[type === "packages" ? "products" : "packages"].length === 0
		// ) {
		// 	dispatch(openDialog("removeProduct"));
		// } else {
		// 	formik.setFieldValue(type, selectedArray);

		// 	if (quoteGroup) {
		// 		props.setNeedsProductRefresh(true);
		// 	}
		// }
	};

	const isTouched = formik.touched.products || formik.touched.packages;
	const isError = Boolean(formik.errors.products || formik.errors.packages);

	const loading =
		productQuery.isLoading || productQuery.isFetching || productQuery.isUninitialized;

	return (
		<>
			<CreateQuoteCardHeader title={'Select Products'} />
			<CardContent>
				<Box pb='1em'>
					{!loading && products.length === 0 && packages.length === 0 ? (
						<Stack>
							<Typography variant='cardContentHeader' mb={'2em'}>
								{'No products available for the selected entity type or occupation'}
							</Typography>
							<Typography variant='subtitle1' mb='1em'>
								{'Please contact Delta for further information**'}
							</Typography>
							<Typography variant='subtitle1'>
								<Link
									href='mailto:underwriting@deltainsurance.com.au'
									target='_blank'
									rel='noopener'
									color='inherit'
								>
									{'underwriting@deltainsurance.com.au'}
								</Link>
								{' (07 3017 4720)'}
							</Typography>
						</Stack>
					) : (
						<Typography variant='cardContentHeader'>
							{'The following products are available for the insured'}
						</Typography>
					)}
				</Box>
				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
						<CircularProgress />
					</Box>
				) : (
					<Grid container spacing={1}>
						{products.map((product) => {
							const isSelected = formik.values.products
								.map((i: { id: any }) => i.id)
								.includes(product.id);
							return (
								<Grid
									item
									xs={12}
									sm={6}
									md={4}
									key={`grid-item-product-${product.id}`}
								>
									<ProductCard
										onClick={
											!readonly && !loadingProducts && !isLoadingUpdate
												? () => selectItem(product, 'products')
												: undefined
										}
										product={product}
										isSelected={isSelected}
										isReadOnly={readonly || loadingProducts || isLoadingUpdate}
										isPackage={false}
									/>
								</Grid>
							);
						})}
						{packages.map((package1) => {
							// const isSelected = props.selectedPackages
							const isSelected = formik.values.packages
								.map((i: { id: any }) => i.id)
								.includes(package1.id);
							return (
								<Grid
									item
									xs={12}
									sm={6}
									md={4}
									key={`grid-package-${package1.id}`}
								>
									<ProductCard
										onClick={
											!readonly && !loadingProducts && !isLoadingUpdate
												? () => selectItem(package1, 'packages')
												: undefined
										}
										product={package1}
										isSelected={isSelected}
										isReadOnly={readonly || loadingProducts || isLoadingUpdate}
										isPackage={true}
									/>
								</Grid>
							);
						})}
					</Grid>
				)}
				{isTouched && isError && (
					<FormHelperText error sx={{ pt: '1em' }}>
						Please select a product or package
					</FormHelperText>
				)}
			</CardContent>
		</>
	);
};
export default ProductSelection;
